import React, { useState } from "react";
import styled from "styled-components";
import { colors2, DropdownSingle, TextInput, UI_BODY_SM_DF } from "oolib";
import { ButtonPrimary } from "oolib/dist/v2/components/Buttons";
import {
  InterfaceContainer,
  InterfaceHeader,
  InterfaceDescription,
  InterfaceActions,
  HelpText,
} from "./BaseIntegrationInterface";

// ============================================
// PLAY STORE ICON
// ============================================
// Google Play mark — white rounded square with the official Google Play 2022
// triangle (four brand-colour facets: red, yellow, blue, green). Triangle paths
// sourced from Wikimedia Commons (public domain); its 28.99x31.99 viewBox is
// nested and centred inside the square.

export const PlayStoreReviewsIcon = ({ size = 32 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="32" height="32" rx="7" fill="#FFFFFF" stroke="#E5E5E5" />
    <svg x="8" y="7.2" width="16" height="17.6" viewBox="0 0 28.99 31.99">
      <path d="M13.54 15.28.12 29.34a3.66 3.66 0 0 0 5.33 2.16l15.1-8.6Z" fill="#EA4335" />
      <path d="m27.11 12.89-6.53-3.74-7.35 6.45 7.38 7.28 6.48-3.7a3.54 3.54 0 0 0 1.5-4.79 3.62 3.62 0 0 0-1.5-1.5z" fill="#FBBC04" />
      <path d="M.12 2.66a3.57 3.57 0 0 0-.12.92v24.84a3.57 3.57 0 0 0 .12.92L14 15.64Z" fill="#4285F4" />
      <path d="m13.64 16 6.94-6.85L5.5.51A3.73 3.73 0 0 0 3.63 0 3.64 3.64 0 0 0 .12 2.65Z" fill="#34A853" />
    </svg>
  </svg>
);

const FieldRow = styled.div`
  margin-top: 16px;
`;

const NoteText = styled(UI_BODY_SM_DF)`
  color: ${colors2.grey60};
  margin-top: 8px;
`;

// ============================================
// INTERFACE
// ============================================

// Google Play package names look like reverse-DNS strings:
// "com.spotify.music", "com.facebook.katana", etc.
const PACKAGE_NAME_REGEX = /^[a-z][a-z0-9_]*(\.[a-z0-9][a-z0-9_]*)+$/i;

// Accepts a full Play Store URL or a raw package name and returns the package
// name ("" if none found). Play Store links look like
// https://play.google.com/store/apps/details?id=com.spotify.music&hl=en — the
// package is the value of the "id" query param.
const extractPlayPackage = (input) => {
  const s = (input || "").trim();
  if (!s) return "";
  if (PACKAGE_NAME_REGEX.test(s)) return s; // already a bare package name
  const m = s.match(/[?&]id=([^&#\s]+)/i); // pulled out of a URL
  return m ? decodeURIComponent(m[1]).trim() : "";
};

// Multiples of 150 — DataForSEO processes/bills Google reviews in 150-row
// batches, so these align exactly (no billing round-up). 1500 is the ceiling
// that completes within the backend's 2-min poll cap (tested).
const REVIEW_COUNT_OPTIONS = [
  { display: "150 most recent", value: 150 },
  { display: "300 most recent", value: 300 },
  { display: "600 most recent", value: 600 },
  { display: "900 most recent", value: 900 },
  { display: "1500 most recent", value: 1500 },
];

// Optional star-rating filter (Play Store only — Apple's API has no equivalent).
// DataForSEO's `rating` takes a single star value; "All ratings" sends nothing
// so the API returns the full mix. Useful for targeting the limited depth at
// just the negative (1-2★) or positive (5★) reviews.
const RATING_OPTIONS = [
  { display: "All ratings", value: 0 },
  { display: "5 stars only", value: 5 },
  { display: "4 stars only", value: 4 },
  { display: "3 stars only", value: 3 },
  { display: "2 stars only", value: 2 },
  { display: "1 star only", value: 1 },
];

export const PlayStoreReviewsInterface = ({ onSubmit, isLoading }) => {
  const [appInput, setAppInput] = useState("");
  const [reviewCount, setReviewCount] = useState(REVIEW_COUNT_OPTIONS[1]); // 300
  const [ratingFilter, setRatingFilter] = useState(RATING_OPTIONS[0]); // All ratings

  // The user may paste the Play Store link OR the bare package name; we extract
  // the package and send only that to the adapter (its contract is unchanged).
  const packageName = extractPlayPackage(appInput);
  const pkgValid = PACKAGE_NAME_REGEX.test(packageName);
  const canSubmit = pkgValid && !isLoading;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      adapterOptions: {
        appId: packageName,
        maxRecords: reviewCount?.value,
        rating: ratingFilter?.value, // 0 = all ratings (BE ignores anything outside 1-5)
      },
    });
  };

  return (
    <InterfaceContainer>
      <InterfaceHeader>Import from Play Store Reviews</InterfaceHeader>
      <InterfaceDescription>
        Pull the most recent user reviews for an Android app. Each review
        becomes one row with rating, content, author, and any developer reply.
      </InterfaceDescription>

      <FieldRow>
        <TextInput
          label="Play Store link or package name"
          placeholder="e.g. https://play.google.com/store/apps/details?id=com.spotify.music"
          value={appInput}
          onChange={(k, v) => setAppInput(v)}
          readOnly={isLoading}
        />
        {appInput.trim() && !pkgValid && (
          <NoteText style={{ color: "#c0392b" }}>
            Could not find a package name. Paste the Play Store link, or the
            package name itself (looks like <strong>com.example.app</strong>).
          </NoteText>
        )}
        {!appInput.trim() && (
          <NoteText>
            Paste the full Play Store link (e.g.{" "}
            <strong>play.google.com/store/apps/details?id=com.spotify.music</strong>)
          </NoteText>
        )}
      </FieldRow>

      <FieldRow>
        <DropdownSingle
          label="Maximum reviews to fetch"
          placeholder="Select a limit..."
          options={REVIEW_COUNT_OPTIONS}
          value={reviewCount}
          onChange={(k, v) => setReviewCount(v)}
          lightboxHeight="small"
          popOutOfOverflowHiddenParent={true}
        />
      </FieldRow>

      <FieldRow>
        <DropdownSingle
          label="Filter by rating (optional)"
          placeholder="All ratings"
          options={RATING_OPTIONS}
          value={ratingFilter}
          onChange={(k, v) => setRatingFilter(v)}
          lightboxHeight="small"
          popOutOfOverflowHiddenParent={true}
        />
      </FieldRow>

      <InterfaceActions>
        <ButtonPrimary
          onClick={handleSubmit}
          disabled={!canSubmit}
          isLoading={isLoading}
        >
          Fetch Reviews
        </ButtonPrimary>
      </InterfaceActions>

      <HelpText>
        Powered by DataForSEO. We handle the credentials on our side. Larger
        imports take longer time to complete — keep this tab open while it runs.
      </HelpText>
    </InterfaceContainer>
  );
};

export const playStoreReviewsIntegrationMeta = {
  id: "playStoreReviews",
  name: "Play Store Reviews",
  description: "Pull recent user reviews for an Android app from the Google Play Store",
};

export default PlayStoreReviewsInterface;
